import {
  Context,
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';

export type DischargeDetailsErrorFields = {
  isPatientSeekingCare: boolean;
  levelOfCare: boolean;
  primaryDischargeReason: boolean;
  secondaryDischargeReason: boolean;
  tentativeDischargeDate: boolean;
  typeOfCare: boolean;
  whoInitiatedDischarge: boolean;
};

export type DischargePlanViewContextValue = {
  errors: { dischargeDetails: DischargeDetailsErrorFields };
  setDischargeDetailsErrors: (errors: DischargeDetailsErrorFields) => void;
};

export const EMPTY_DISCHARGE_PLAN_VIEW_CONTEXT: DischargePlanViewContextValue =
  {
    errors: {
      dischargeDetails: {
        isPatientSeekingCare: false,
        levelOfCare: false,
        primaryDischargeReason: false,
        secondaryDischargeReason: false,
        tentativeDischargeDate: false,
        typeOfCare: false,
        whoInitiatedDischarge: false,
      },
    },
    setDischargeDetailsErrors: () => {},
  };

export const DischargePlanViewContext: Context<DischargePlanViewContextValue> =
  createContext<DischargePlanViewContextValue>(
    EMPTY_DISCHARGE_PLAN_VIEW_CONTEXT,
  );

// Gives context to the DischargePlanView and its sections.
export const DischargePlanViewContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [dischargeDetailsErrors, setDischargeDetailsErrors] =
    useState<DischargeDetailsErrorFields>(
      EMPTY_DISCHARGE_PLAN_VIEW_CONTEXT.errors.dischargeDetails,
    );

  return (
    <DischargePlanViewContext.Provider
      value={{
        errors: { dischargeDetails: dischargeDetailsErrors },
        setDischargeDetailsErrors: (errors) =>
          setDischargeDetailsErrors(errors),
      }}
    >
      {children}
    </DischargePlanViewContext.Provider>
  );
};

export const useDischargePlanViewContext =
  (): DischargePlanViewContextValue => {
    const context = useContext(DischargePlanViewContext);
    if (!context) {
      throw new Error(
        'useDischargePlanViewContext must be used within a DischargePlanViewContextProvider.',
      );
    }
    return context;
  };
